<template>
  <modal :show.sync="show" modalClasses="modal-hide-footer">
    <h3 slot="header" class="mb-0">Directorships And Business Interests</h3>
    <b-row>
      <b-col cols="12">
          <!-- Summary Table -->
          <h4 style="margin-bottom: 10px">B1: DIRECTORSHIPS AND BUSINESS INTERESTS</h4>
          <h5 style="margin-top: 20px; margin-bottom: 10px;">SUMMARY</h5>
          <table class="directories-table bordered summary-table" style="width: 100%">
            <thead>
              <tr>
                <th style="width: 60px">No.</th>
                <th>Company</th>
                <th>Position</th>
                <th>Appoint</th>
                <th>Resign Date</th>
                <th>Profit After Tax (RM)</th>
                <th>Year</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(business, index) in normalizedDirectoriesBusiness"
                :key="'summary-' + index"
              >
                <td style="text-align: center">{{ index + 1 }}.</td>
                <td>{{ safeDisplay(business.company_name) }}</td>
                <td>{{ safeDisplay(business.position) }}</td>
                <td style="text-align: center">{{ formatDate(business.appointment_date) }}</td>
                <td style="text-align: center">{{ formatDate(business.resignation_date) }}</td>
                <td style="text-align: center">{{ safeDisplay(business.profit_after_tax) }}</td>
                <td style="text-align: center">{{ safeDisplay(business.latest_financial_year) }}</td>
                <td style="text-align: center">{{ safeDisplay(business.status) }}</td>
              </tr>
              <tr v-if="normalizedDirectoriesBusiness.length === 0">
                <td colspan="8" class="text-center">No Information Available</td>
              </tr>
            </tbody>
          </table>

          <!-- Detailed Business Interests Data -->
          <div class="detailed-business-interests" style="margin-top: 30px">
            <div v-if="normalizedDirectoriesBusiness.length > 0">
              <div
                v-for="(business, index) in normalizedDirectoriesBusiness"
                :key="index"
                class="business-detail"
                style="margin-bottom: 30px; page-break-inside: avoid"
              >
                <!-- Business Header -->
                <div
                  class="business-header"
                  style="
                    background: #f5f5f5;
                    padding: 10px;
                    border-left: 4px solid #ccc;
                  "
                >
                  <strong>{{ index + 1 }}. {{ safeDisplay(business.company_name) }} ({{ safeDisplay(business.local_no) }}/ {{ safeDisplay(business.registration_no) }})</strong>
                </div>

                <!-- Business Details Table -->
                <table
                  class="directories-table bordered"
                  style="width: 100%; margin-bottom: 15px"
                >
                  <tbody>
                    <tr>
                      <td style="width: 150px; background: #e9e9e9">
                        <strong>Status</strong>
                      </td>
                      <td colspan="3">{{ safeDisplay(business.status) }}</td>
                    </tr>
                    <tr>
                      <td style="background: #e9e9e9">
                        <strong>Nature Of Business</strong>
                      </td>
                      <td colspan="3">{{ safeDisplay(business.business_objects) }}</td>
                    </tr>
                    
                    <!-- Incorporation/Registration and Expiry Date side by side -->
                    <tr>
                      <td style="background: #e9e9e9">
                        <strong>{{ business.incorporation_date ? 'Incorporation' : 'Registration' }}</strong>
                      </td>
                      <td>{{ formatDate(business.incorporation_date || business.registration_date) }}</td>
                      <td style="background: #e9e9e9">
                        <strong>{{ business.paid_up_capital ? 'Paid-Up' : 'Expiry Date' }}</strong>
                      </td>
                      <td>
                        {{ business.paid_up_capital ? formatCurrency(business.paid_up_capital) : formatDate(business.expiry_date) }}
                      </td>
                    </tr>

                    <!-- CCM Dated and Shares side by side -->
                    <tr>
                      <td style="background: #e9e9e9">
                        <strong>CCM Dated</strong>
                      </td>
                      <td>
                        {{ formatDate(business.cpo_date) }} 
                        {{ business.cpo_date && business.company_name === 'SIMFONI ASIA HOLIDAYS SDN BHD' ? 'SHOW LAST FILLING AT CCM WAS ON' : '' }}
                      </td>
                      <td style="background: #e9e9e9">
                        <strong>{{ business.shares ? 'Shares' : 'CCM Date' }}</strong>
                      </td>
                      <td>
                        {{ business.shares ? formatCurrency(business.shares) : formatDate(business.cpo_date) }}
                      </td>
                    </tr>

                    <!-- Name (full width) -->
                    <tr>
                      <td style="background: #e9e9e9">
                        <strong>Name</strong>
                      </td>
                      <td colspan="3">{{ safeDisplay(business.name) }}</td>
                    </tr>

                    <!-- New ID/Passport and Old ID side by side -->
                    <tr>
                      <td style="background: #e9e9e9">
                        <strong>New ID / Passport</strong>
                      </td>
                      <td>{{ safeDisplay(business.ic_passport) }}</td>
                      <td style="background: #e9e9e9">
                        <strong>Old ID</strong>
                      </td>
                      <td>-</td>
                    </tr>

                    <!-- Position and Appointed/Resigned side by side -->
                    <tr>
                      <td style="background: #e9e9e9">
                        <strong>Position</strong>
                      </td>
                      <td>{{ safeDisplay(business.position) }}</td>
                      <td style="background: #e9e9e9">
                        <strong>{{ business.appointment_date ? 'Appointed' : 'CCM Date' }}</strong>
                      </td>
                      <td>
                        {{ business.appointment_date ? formatDate(business.appointment_date) : formatDate(business.cpo_date) }}
                      </td>
                    </tr>

                    <!-- Shares/Resigned and Address side by side -->
                    <tr>
                      <td style="background: #e9e9e9">
                        <strong>{{ business.shares ? 'Shares' : 'Resigned' }}</strong>
                      </td>
                      <td>
                        {{ business.shares ? formatCurrency(business.shares) : formatDate(business.resignation_date) }}
                      </td>
                      <td style="background: #e9e9e9">
                        <strong>Address</strong>
                      </td>
                      <td>{{ safeDisplay(business.address) }}</td>
                    </tr>

                    <!-- Remark (full width) -->
                    <tr>
                      <td style="background: #e9e9e9">
                        <strong>Remark</strong>
                      </td>
                      <td colspan="3">{{ safeDisplay(business.remark) || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="no-data-section">
              <p class="text-xs">No Information Available</p>
            </div>
          </div>

          <!-- Address Records Section -->
          <div class="detailed-address-records" style="margin-top: 30px">
            <h4 style="margin-bottom: 20px">B2: ADDRESS RECORDS</h4>
            <table class="directories-table bordered summary-table" style="width: 100%">
              <thead>
                <tr>
                  <th>Address</th>
                  <th style="width: 120px">Last Updated</th>
                  <th style="width: 100px">Source</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(address, index) in normalizedAddressRecords"
                  :key="'address-' + index"
                >
                  <td>{{ safeDisplay(address.address) }}</td>
                  <td style="text-align: center">{{ formatDate(address.last_updated) }}</td>
                  <td style="text-align: center">{{ safeDisplay(address.source) }}</td>
                </tr>
                <tr v-if="normalizedAddressRecords.length === 0">
                  <td colspan="3" class="text-center">No Information Available</td>
                </tr>
              </tbody>
            </table>
          </div>
      </b-col>
    </b-row>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import { Table, TableColumn } from "element-ui";

export default {
  name: "DirectoriesBusinessModal",
  props: {
    directoriesBusiness: {
      type: Array,
      default: () => [],
    },
    addressRecords: {
      type: Array,
      default: () => [],
    },
    show_directories_full_view: Boolean,
  },
  components: {
    Modal,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  computed: {
    show: {
      get() {
        return this.show_directories_full_view;
      },
      set(value) {
        this.$emit("update:show_directories_full_view", value);
      },
    },
    normalizedDirectoriesBusiness() {
      if (Array.isArray(this.directoriesBusiness)) {
        return this.directoriesBusiness;
      } else if (
        this.directoriesBusiness &&
        typeof this.directoriesBusiness === "object"
      ) {
        return [this.directoriesBusiness];
      }
      return [];
    },
    normalizedAddressRecords() {
      // Use the addressRecords prop directly
      if (Array.isArray(this.addressRecords) && this.addressRecords.length > 0) {
        return this.addressRecords;
      }
      
      // Fallback to empty array if no address records provided
      return [];
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '-';
      
      try {
        // Check if date is already in dd-mm-yyyy format (like "30-03-2010")
        if (typeof dateString === 'string' && dateString.includes('-')) {
          const parts = dateString.split('-');
          
          // If it's in dd-mm-yyyy format, return as is
          if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
            return dateString;
          }
          
          // If it's in yyyy-mm-dd format, convert to dd-mm-yyyy
          if (parts.length === 3 && parts[0].length === 4 && parts[1].length === 2 && parts[2].length === 2) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
          }
        }
        
        // Try to parse as a Date object for other formats
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }).replace(/\//g, '-');
        }
        
        // If all parsing fails, return the original string
        return dateString;
      } catch (e) {
        console.warn('Date parsing error for:', dateString, e);
        return dateString;
      }
    },
    
    formatCurrency(value) {
      if (!value) return '-';
      
      // Format number with commas for thousands
      return parseFloat(value).toLocaleString('en-MY', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    // Safe display method to handle empty values
    safeDisplay(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      
      // Handle empty arrays
      if (Array.isArray(value) && value.length === 0) {
        return '-';
      }
      
      // Handle arrays with content
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      
      // Handle empty objects
      if (typeof value === 'object' && Object.keys(value).length === 0) {
        return '-';
      }
      
      // Handle string representations of empty arrays/objects
      if (typeof value === 'string' && (value === '[]' || value === '{}')) {
        return '-';
      }
      
      return value;
    }
  },
};
</script>

<style scoped>
/* Styles for the plain HTML tables matching legal cases style */
.directories-table {
  border-collapse: collapse;
  width: 100%;
  font-family: inherit;
}

.directories-table.bordered,
.directories-table th,
.directories-table td {
  border: 1px solid #000 !important;
}

/* Summary table specific header styling */
.summary-table th {
  background-color: #252938 !important;
  color: #fff !important;
  border: none !important;
  vertical-align: top !important;
  text-transform: none !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 19.5px !important;
  padding: 9px 12px !important;
}

/* Regular table header styling (for detailed tables) */
.directories-table:not(.summary-table) th {
  background-color: #a9a9a9 !important;
  background-image: none !important;
  color: #000 !important;
  font-weight: 700 !important;
  text-align: left;
  padding: 8px 6px;
}

.directories-table td {
  background-color: #fff !important;
  color: #000 !important;
  padding: 8px 6px;
  text-align: left;
}

.directories-table td:first-child {
  text-align: left;
}

.directories-table .text-break {
  white-space: normal;
}

/* SAS specific styles */
.note-section {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 12px 15px;
}

.no-data-section {
  background-color: #f8f9fa;
  padding: 12px;
  text-align: center;
  border-radius: 4px;
}

.company-name {
  word-break: break-word;
  line-height: 1.2;
}

.text-xs {
  font-size: 0.75rem;
}

.table-container {
  width: 100%;
  overflow-x: auto;
}

/* Detailed business interests styling */
.detailed-business-interests {
  margin-top: 30px;
}

.detailed-address-records {
  margin-top: 30px;
}

.business-detail {
  margin-bottom: 30px;
  page-break-inside: avoid;
}

.business-header {
  background: #f5f5f5;
  padding: 10px;
  border-left: 4px solid #ccc;
  font-size: 16px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-container {
    font-size: 0.7rem;
  }
  
  .directories-table th,
  .directories-table td {
    padding: 6px 4px;
  }
}
</style>